import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import classNames from 'classnames';
import Cropper, { Area, Point } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';

import { FormErrorHelperText, ModalTitle, UploadButton } from 'components';
import { getCroppedImg } from 'utils';

import styles from './FormMediaUpload.module.scss';

type FormMediaUploadProps = {
  title: string;
  src?: string;
  disabled?: boolean;
  onlyView?: boolean;
  onInputChange: (file: File) => void;
  onRemove: () => void;
};

const acceptFormats: string[] = ['.jpg', '.jpeg', '.png'];

export function FormMediaUpload({
  onInputChange,
  onRemove,
  src,
  title,
  disabled,
  onlyView,
}: FormMediaUploadProps): JSX.Element {
  const { t } = useTranslation('providerRoom');
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const [imageSrc, setImageSrc] = useState<string | undefined>(src);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [openCropImageDialog, setOpenCropImageDialog] = useState<boolean>(false);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [area, setArea] = useState<Area | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [cropSize, setCropSize] = useState<{ width: number; height: number }>({ width: 220, height: 220 });
  const cropContainerRef = useRef<HTMLDivElement | null>(null);

  const validate = (file: File): boolean => {
    let isValid = false;
    const fileSize = file.size / 1024 / 1024; // in MB
    if (fileSize > 10) {
      setError(t('fileTooLargeCannotUploadMax10'));
    } else {
      setError('');
      isValid = true;
    }
    return isValid;
  };

  const imageSrcToShowOnForm = useMemo(() => {
    return openCropImageDialog ? src : imageSrc;
  }, [src, imageSrc, openCropImageDialog]);

  useLayoutEffect(() => {
    if (!openCropImageDialog) return;

    const element = cropContainerRef.current;
    if (!element) return;

    const updateSize = () => {
      const width = element.clientWidth;
      const height = element.clientHeight;
      if (!width || !height) return;
      const max = Math.min(width, height) - 40;
      const size = Math.max(140, Math.min(220, max));
      setCropSize({ width: size, height: size });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [openCropImageDialog]);

  const handleInputChange = useCallback((e: any) => {
    if (!e.target.files.length) {
      return;
    }
    const file = e.target.files[0] as File;

    if (validate(file)) {
      setImageSrc(URL.createObjectURL(file));
      setOpenCropImageDialog(true);
    }
  }, []);

  const handleAcceptImage = useCallback(async () => {
    if (!imageSrc || !area) {
      return;
    }

    setLoading(true);
    const resultImgBlob = await getCroppedImg(imageSrc, area);
    if (!resultImgBlob) {
      setLoading(false);
      throw Error('Error handling image crop');
    }

    const file = new File([resultImgBlob], 'avatar.png');
    onInputChange(file);
    setImageSrc(URL.createObjectURL(file));
    setOpenCropImageDialog(false);
    setLoading(false);
  }, [imageSrc, area, onInputChange]);

  const handleRemove = () => {
    setImageSrc('');
    setError('');
    onRemove();
  };

  const handleClose = () => {
    setImageSrc(src);
    setError('');
    setOpenCropImageDialog(false);
  };

  return (
    <>
      <Stack className={styles.container}>
        <Box className={styles.imageContainer}>
          {imageSrcToShowOnForm ? (
            <img className={styles.image} src={imageSrcToShowOnForm} alt={title} loading='lazy' />
          ) : (
            <Typography component='p' variant='body' align='center'>
              {title}
            </Typography>
          )}
        </Box>

        {!onlyView && (
          <>
            <Stack direction='column' justifyContent='space-between' className={styles.rightColumn}>
              <UploadButton
                onInputChange={handleInputChange}
                acceptFormats={acceptFormats.join(', ')}
                disabled={disabled}
                className={styles.upload}
              />
              <Stack textAlign='start' className={styles.helperText}>
                <Typography variant={isDesktop ? 'body6' : 'body1'} color='text.secondary'>
                  {t('maxFileSize10Mb')}
                </Typography>
                <Typography variant={isDesktop ? 'body6' : 'body1'} color='text.secondary'>
                  {t('acceptableFormatsJpgPng')}
                </Typography>
              </Stack>
            </Stack>

            <Typography
              variant={isDesktop ? 'body6' : 'body2'}
              color='#979797'
              component='u'
              className={classNames(styles.remove, imageSrc ? '' : styles.removeHidden)}
              onClick={handleRemove}
            >
              {t('remove')}
            </Typography>
            <FormErrorHelperText className={styles.errorHelper}>{error}</FormErrorHelperText>
          </>
        )}
      </Stack>

      <Dialog open={openCropImageDialog}>
        <DialogTitle>
          <ModalTitle title={t('crop')} onClose={handleClose} />
        </DialogTitle>

        <DialogContent
          sx={{
            width: 350,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            backgroundColor: 'var(--app-surface)',
          }}
        >
          <Box ref={cropContainerRef} sx={{ position: 'relative', width: '100%', height: '300px', overflow: 'hidden' }}>
            <Cropper
              image={imageSrc}
              crop={crop}
              minZoom={0.1}
              objectFit='contain'
              restrictPosition={false}
              zoomSpeed={0.05}
              cropShape='rect'
              showGrid={true}
              cropSize={cropSize}
              style={{
                containerStyle: {
                  width: '100%',
                  height: '300px',
                  position: 'relative',
                },
              }}
              zoom={zoom}
              aspect={1}
              onCropChange={(crop) => {
                setCrop(crop);
              }}
              onMediaLoaded={(media) => {
                const minZoom = Math.max(cropSize.width / media.naturalWidth, cropSize.height / media.naturalHeight);
                if (zoom < minZoom) {
                  setZoom(minZoom);
                }
              }}
              onCropComplete={(_, croppedAreaPixels: Area) => {
                setArea(croppedAreaPixels);
              }}
              onZoomChange={setZoom}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${cropSize.width}px`,
                height: `${cropSize.height}px`,
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${cropSize.width}px`,
                height: `${cropSize.height}px`,
                transform: 'translate(-50%, -50%)',
                borderRadius: '50%',
                border: '2px dashed var(--app-brand-primary)',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: `${cropSize.width}px`,
                height: `${cropSize.height}px`,
                transform: 'translate(-50%, -50%)',
                border: '1px solid rgba(255,255,255,0.4)',
                pointerEvents: 'none',
              }}
            />
          </Box>
          <Slider
            value={zoom}
            onChange={(_, value) => setZoom(value as number)}
            min={0.1}
            max={3}
            step={0.01}
            size='small'
            aria-label={t('aria.zoom')}
          />
          <Typography variant='body2' sx={{ color: 'var(--app-text-muted)' }}>
            {t('imageCropInfo')}
          </Typography>

          <Stack sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Button variant='outlined' disabled={loading} onClick={handleClose}>
              {t('decline')}
            </Button>

            <Button variant='contained' disabled={loading} onClick={handleAcceptImage}>
              {t('accept')}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
